import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { User as UserIcon, Star, Save, HelpCircle, Heart, BookOpen, MessageCircle, BookMarked, Shield, Bell, Sparkles } from "lucide-react";
import PushNotificationToggle from "@/components/PushNotificationToggle";

const Profile = () => {
  const { user: currentUser, language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    language: "es"
  });
  const [loading, setLoading] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  
  // Admin emails that can see feedback panel
  const adminEmails = ["cbayes86@hotmail.com"];
  const isAdmin = currentUser && adminEmails.includes(currentUser.email);

  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.name || "",
        gender: currentUser.gender || "",
        language: currentUser.language || "es"
      });
    }
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API}/auth/update-profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh user data from server
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update context (assuming setUser is available)
      if (window.location.reload) {
        // Force reload to update context
        toast.success(t("Perfil actualizado", "Perfil actualitzat"));
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(t("Error al actualizar perfil", "Error en actualitzar perfil"));
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  // Carta natal temporalment desactivada - Fase 2

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div data-testid="profile-header" className="mb-12 fade-in">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
              <UserIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold glow-text">
                {t("Mi Perfil", "El Meu Perfil")}
              </h1>
              <p className="text-gray-400 mt-1">{currentUser?.email}</p>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="glass rounded-3xl p-8 border border-teal-400/20 mb-8 fade-in" style={{animationDelay: '0.1s'}}>
          <form data-testid="profile-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div>
              <h2 className="text-2xl font-semibold mb-6 text-teal-200">
                {t("Información Básica", "Informació Bàsica")}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    {t("Nombre", "Nom")}
                  </label>
                  <input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-300 mb-2">
                    {t("Género", "Gènere")}
                  </label>
                  <select
                    id="gender"
                    data-testid="gender-select"
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                  >
                    <option value="">{t("Seleccionar", "Seleccionar")}</option>
                    <option value="female">{t("Femenino", "Femení")}</option>
                    <option value="male">{t("Masculino", "Masculí")}</option>
                    <option value="other">{t("Otro", "Altre")}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="language" className="block text-sm font-medium text-gray-300 mb-2">
                    {t("Idioma", "Idioma")}
                  </label>
                  <select
                    id="language"
                    data-testid="language-select"
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                  >
                    <option value="es">Español</option>
                    <option value="ca">Català</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="pt-6">
              <button
                data-testid="save-profile-btn"
                type="submit"
                disabled={loading}
                className="w-full md:w-auto btn-primary py-3 px-8 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                <span>{loading ? t("Guardando...", "Desant...") : t("Guardar Cambios", "Desar Canvis")}</span>
              </button>
            </div>
          </form>

          {/* Informació de l'App - FORA DEL FORM */}
          <div className="pt-6 border-t border-gray-700">
            <div className="flex items-center gap-3 mb-6">
              <Star className="w-6 h-6 text-purple-400" />
              <h2 className="text-2xl font-semibold text-purple-300">
                {t("Sobre ESSENC·IA", "Sobre ESSENC·IA")}
              </h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-4">
              {t(
                "ESSENC·IA es tu espacio de automaestría emocional consciente. Aquí aprendes a jugar con tus emociones como herramienta de crecimiento personal.",
                "ESSENC·IA és el teu espai d'autoconeixement emocional conscient. Aquí aprens a jugar amb les teves emocions com a eina de creixement personal."
              )}
            </p>

            <p className="text-teal-300/80 italic text-sm">
              {t(
                "Las emociones son las herramientas de tu viaje consciente. Cada paso que das aquí te prepara para crecer y expandirte.",
                "Les emocions són les eines del teu viatge conscient. Cada pas que fas aquí et prepara per créixer i expandir-te."
              )}
            </p>
          </div>
          {/* Push Notifications */}
          <div className="pt-6 border-t border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <Bell className="w-5 h-5 text-teal-300" />
              <h2 className="text-lg font-semibold text-gray-200">
                {t("Recordatorio diario", "Recordatori diari")}
              </h2>
            </div>
            <p className="text-sm text-gray-400 mb-3">
              {t("Recibe una notificación cada mañana a las 9h", "Rep una notificació cada matí a les 9h")}
            </p>
            <PushNotificationToggle />
          </div>



          {/* Admin Section - Only visible for admin users */}
          {isAdmin && (
            <div className="pt-6 border-t border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-amber-400" />
                <h2 className="text-xl font-semibold text-amber-300">
                  {t("Panel de Administración", "Panell d'Administració")}
                </h2>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  data-testid="admin-panel-btn"
                  onClick={() => navigate('/admin')}
                  className="glass rounded-xl p-4 border border-amber-400/20 hover:border-amber-400/40 transition-all text-left"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="font-semibold text-amber-200">
                        {t("Usuarios y subscripciones", "Usuaris i subscripcions")}
                      </p>
                      <p className="text-sm text-gray-400">
                        {t("Estado, prueba, activos, expirados", "Estat, prova, actius, expirats")}
                      </p>
                    </div>
                  </div>
                </button>
                <button
                  data-testid="admin-feedback-btn"
                  onClick={() => navigate('/admin/feedback')}
                  className="glass rounded-xl p-4 border border-amber-400/20 hover:border-amber-400/40 transition-all text-left"
                >
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="font-semibold text-amber-200">
                        {t("Ver Feedback", "Veure Feedback")}
                      </p>
                      <p className="text-sm text-gray-400">
                        {t("Revisa los comentarios de los usuarios", "Revisa els comentaris dels usuaris")}
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Com funciona - How it Works Section - FORA DEL FORM */}
          <div className="pt-6 border-t border-gray-700">
              <button
                data-testid="toggle-how-it-works"
                onClick={() => setShowHowItWorks(!showHowItWorks)}
                className="flex items-center gap-3 w-full text-left"
              >
                <HelpCircle className="w-6 h-6 text-teal-400" />
                <h2 className="text-2xl font-semibold text-teal-300 flex-1">
                  {t("¿Cómo funciona?", "Com funciona?")}
                </h2>
                <span className={`text-gray-400 transition-transform ${showHowItWorks ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>

              {showHowItWorks && (
                <div className="mt-6 space-y-4 fade-in">
                  {/* Intro */}
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {t(
                      "ESSENC·IA es una app autónoma de automaestría emocional. No hay un camino único - tú marcas tu ritmo. Aquí tienes las principales herramientas:",
                      "ESSENC·IA és una app autònoma d'autoconeixement emocional. No hi ha un camí únic - tu marques el teu ritme. Aquí tens les principals eines:"
                    )}
                  </p>

                  {/* Tools Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Daily Feeling */}
                    <div className="glass rounded-xl p-4 border border-pink-400/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="w-5 h-5 text-pink-400" />
                        <h3 className="font-semibold text-pink-300">
                          {t("Sentir Diario", "Sentir Diari")}
                        </h3>
                      </div>
                      <p className="text-gray-400 text-sm">
                        {t(
                          "Cada día, comparte cómo te sientes. La IA te dará una reflexión personalizada que podrás revisar en tu historial.",
                          "Cada dia, comparteix com et sents. La IA et donarà una reflexió personalitzada que podràs revisar al teu historial."
                        )}
                      </p>
                    </div>

                    {/* Lessons */}
                    <div className="glass rounded-xl p-4 border border-teal-400/10">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-5 h-5 text-teal-400" />
                        <h3 className="font-semibold text-teal-300">
                          {t("Lecciones", "Lliçons")}
                        </h3>
                      </div>
                      <p className="text-gray-400 text-sm">
                        {t(
                          "Contenido profundo sobre automaestría emocional. Se van desbloqueando a medida que completas las anteriores.",
                          "Contingut profund sobre autoconeixement emocional. Es van desbloquejant a mesura que completes les anteriors."
                        )}
                      </p>
                    </div>

                    {/* Exercises */}
                    <div className="glass rounded-xl p-4 border border-purple-400/10">
                      <div className="flex items-center gap-2 mb-2">
                        <BookMarked className="w-5 h-5 text-purple-400" />
                        <h3 className="font-semibold text-purple-300">
                          {t("Ejercicios", "Exercicis")}
                        </h3>
                      </div>
                      <p className="text-gray-400 text-sm">
                        {t(
                          "Prácticas guiadas para trabajar tus emociones. Cada semana puedes desbloquear uno nuevo.",
                          "Pràctiques guiades per treballar les teves emocions. Cada setmana pots desbloquejar-ne un de nou."
                        )}
                      </p>
                    </div>

                    {/* Chat IA */}
                    <div className="glass rounded-xl p-4 border border-slate-400/10">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageCircle className="w-5 h-5 text-slate-400" />
                        <h3 className="font-semibold text-slate-300">
                          {t("Chat con la IA", "Xat amb la IA")}
                        </h3>
                      </div>
                      <p className="text-gray-400 text-sm">
                        {t(
                          "Conversa cuando quieras con tu guía IA. Puede ayudarte a ordenar tus emociones y profundizar en lo que sientes.",
                          "Conversa quan vulguis amb la teva guia IA. Pot ajudar-te a ordenar les teves emocions i aprofundir en el que sents."
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Final tip */}
                  <div className="bg-teal-900/20 rounded-xl p-4 border border-teal-400/20">
                    <p className="text-teal-200 text-sm">
                      💡 {t(
                        "Consejo: No hay prisa. Usa la app a tu ritmo. La IA se adapta a tu nivel de consciencia.",
                        "Consell: No hi ha pressa. Usa l'app al teu ritme. La IA s'adapta al teu nivell de consciència."
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
        </div>

        {/* Info Box */}
        <div className="glass rounded-2xl p-6 border border-purple-400/10 fade-in" style={{animationDelay: '0.2s'}}>
          <h3 className="text-lg font-semibold mb-3 text-purple-300">
            {t("Tu Guía IA", "La Teva Guia IA")}
          </h3>
          <p className="text-gray-300 leading-relaxed">
            {t(
              "La IA consciente de ESSENC·IA está diseñada para guiarte en tu camino de automaestría emocional. Utiliza el chat para explorar tus emociones, recibir perspectivas profundas y descubrir nuevas formas de relacionarte con tu mundo interior.",
              "La IA conscient d'ESSENC·IA està dissenyada per guiar-te en el teu camí d'autoconeixement emocional. Utilitza el xat per explorar les teves emocions, rebre perspectives profundes i descobrir noves formes de relacionar-te amb el teu món interior."
            )}
          </p>
        </div>
      </div>

      {/* Affiliate banner Emergent - discret, només per a usuaris autenticats */}
      <div className="mt-8">
        <a
          href="https://app.emergent.sh/?via=cristina"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="profile-emergent-affiliate"
          className="block group"
        >
          <div className="glass rounded-2xl p-4 sm:p-5 border border-slate-700/40 hover:border-teal-400/40 transition-all">
            <div className="flex items-center gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500/20 to-purple-500/20 border border-teal-400/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-teal-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-200">
                  {t("¿Quieres crear tu propia app?", "Vols crear la teva pròpia app?")}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {t(
                    "ESSENC·IA fue construida con Emergent. Descúbrelo →",
                    "ESSENC·IA es va construir amb Emergent. Descobreix-ho →"
                  )}
                </p>
              </div>
              <span className="hidden sm:inline-block text-[10px] uppercase tracking-wider text-teal-400/70 group-hover:text-teal-300 transition-colors">
                -5%
              </span>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
};

export default Profile;
